import { z } from 'zod';
import { founderStageEnum } from './audiences.js';
export const coachingCallTypeEnum = z.enum(['kick_off', 'masterclass', 'office_hours', 'coaching']);
// Backed by the task_recurrence enum at the DB level (identical schedule
// semantics) but coaching calls do NOT support the free-text `custom` option —
// only the preset schedules. A NULL/omitted value means a one-off call.
export const coachingCallRecurrenceEnum = z.enum(['daily', 'weekly', 'monthly']);
const isoDatetime = z.string().datetime({ offset: true });
const optionalUuid = z.string().uuid().nullish();
const optionalText = (max) => z.string().trim().min(1).max(max).nullish();
export const coachingCallCreateSchema = z
    .object({
    title: z.string().min(1).max(500),
    description: z.string().max(20_000).nullish(),
    starts_at: isoDatetime,
    ends_at: isoDatetime,
    call_type: coachingCallTypeEnum.optional(),
    coach_id: optionalUuid,
    coach_author_id: optionalUuid,
    coach_name: optionalText(200),
    coach_initials: optionalText(12),
    coach_avatar_url: z.string().url().max(2048).nullish(),
    join_url: z.string().url().max(2048).nullish(),
    calendly_url: z.string().url().max(2048).nullish(),
    // Extended = Calendly's event-details panel is shown and the booking dialog
    // widens to fit it. Omitted falls back to the column default (true).
    calendly_extended: z.boolean().optional(),
    founder_stages: z
        .array(founderStageEnum)
        .max(3)
        .refine((s) => new Set(s).size === s.length, 'founder_stages must be unique')
        .optional(),
    recurrence: coachingCallRecurrenceEnum.nullish(),
})
    .strict()
    .refine((data) => Boolean(data.coach_id || data.coach_author_id || data.coach_name), {
    message: 'coach_id, coach_author_id, or coach_name is required',
    path: ['coach_id'],
})
    .refine((data) => new Date(data.ends_at) > new Date(data.starts_at), {
    message: 'ends_at must be after starts_at',
    path: ['ends_at'],
});
// Mirror the partial pattern from coaching-call-recordings: unwrap the refine so
// .partial() works on the underlying object, then re-apply the cross-field check
// only when both ends are present.
const coachingCallBaseSchema = z
    .object({
    title: z.string().min(1).max(500),
    description: z.string().max(20_000).nullish(),
    starts_at: isoDatetime,
    ends_at: isoDatetime,
    call_type: coachingCallTypeEnum.optional(),
    coach_id: optionalUuid,
    coach_author_id: optionalUuid,
    coach_name: optionalText(200),
    coach_initials: optionalText(12),
    coach_avatar_url: z.string().url().max(2048).nullish(),
    join_url: z.string().url().max(2048).nullish(),
    calendly_url: z.string().url().max(2048).nullish(),
    // Extended = Calendly's event-details panel is shown and the booking dialog
    // widens to fit it. Omitted falls back to the column default (true).
    calendly_extended: z.boolean().optional(),
    founder_stages: z
        .array(founderStageEnum)
        .max(3)
        .refine((s) => new Set(s).size === s.length, 'founder_stages must be unique')
        .optional(),
    recurrence: coachingCallRecurrenceEnum.nullish(),
})
    .strict();
export const coachingCallUpdateSchema = coachingCallBaseSchema.partial().refine((data) => {
    if (data.starts_at && data.ends_at) {
        return new Date(data.ends_at) > new Date(data.starts_at);
    }
    return true;
}, { message: 'ends_at must be after starts_at', path: ['ends_at'] });
//# sourceMappingURL=coaching-calls.js.map